import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { bool, func, number, shape, string } from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Impersonation from 'services/api/Impersonation';
import authActions from 'store/app/auth/action';
import { I18n } from 'utils/i18n';
import useActions from 'utils/useActions';
import {
  getWebAuthnError,
  isWebAuthnSupported,
  preparePublicKeyCredential,
  preparePublicKeyOptions,
} from 'utils/webAuthn';

const ROLE_SUPER_ADMIN = 2;

const ImpersonationStepUpDialog = ({
  open = false,
  onClose,
  targetUser: targetUserProp = null,
  targetEmployee = null,
  onSuccess = undefined,
  resetCompany = false,
}) => {
  const targetUser = targetEmployee?.user || targetUserProp;
  const completeImpersonationStart = useActions(authActions.completeImpersonationStart);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [stepUpOptions, setStepUpOptions] = useState(null);
  const passwordInputRef = useRef();

  const resetForm = useCallback(() => {
    setPassword('');
    setError('');
    setLoading(false);
    setPasskeyAvailable(false);
    setStepUpOptions(null);
  }, []);

  useEffect(() => {
    if (!open) {
      resetForm();
      return undefined;
    }
    setTimeout(() => {
      passwordInputRef.current?.focus();
    }, 100);

    let cancelled = false;
    const loadStepUp = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await Impersonation.beginStepUp();
        if (cancelled) return;
        const data = response?.data || {};
        setPasskeyAvailable(!!data.passkey && isWebAuthnSupported());
        setStepUpOptions(data);
      } catch (err) {
        if (!cancelled) {
          setError(err?.data?.error?.message || I18n.t('Impersonation.StepUpFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadStepUp();
    return () => {
      cancelled = true;
    };
  }, [open, resetForm]);

  const handleStart = useCallback(
    async ({ password: stepUpPassword, assertion } = {}) => {
      if (!targetUser?._id) return;
      if (targetUser.role === ROLE_SUPER_ADMIN) {
        setError(I18n.t('Impersonation.CannotImpersonateSuperadmin'));
        return;
      }
      setLoading(true);
      setError('');
      try {
        const response = await Impersonation.start({
          targetUserId: targetUser._id,
          password: stepUpPassword,
          assertion,
        });
        const { token, user } = response?.data || {};
        if (!token?.hash) {
          throw new Error(I18n.t('Impersonation.StartFailed'));
        }
        completeImpersonationStart({
          token,
          targetUser: user,
          expiresAt: token.expiresAt,
          resetCompany,
        });
        onSuccess?.({ token, user });
        onClose();
      } catch (err) {
        setError(
          err?.data?.error?.message ||
            (stepUpPassword
              ? I18n.t('Impersonation.InvalidPassword')
              : I18n.t('Impersonation.StepUpFailed')),
        );
      } finally {
        setLoading(false);
      }
    },
    [completeImpersonationStart, onClose, onSuccess, resetCompany, targetUser],
  );

  const handlePasswordSubmit = useCallback(
    event => {
      event.preventDefault();
      if (!password.trim()) {
        setError(I18n.t('Impersonation.PasswordRequired'));
        return;
      }
      handleStart({ password });
    },
    [handleStart, password],
  );

  const handlePasskey = useCallback(async () => {
    if (!stepUpOptions?.options) return;
    setLoading(true);
    setError('');
    try {
      const publicKeyOptions = preparePublicKeyOptions(stepUpOptions.options);
      const credential = await navigator.credentials.get({ publicKey: publicKeyOptions });
      if (!credential) {
        throw new Error(I18n.t('Impersonation.StepUpFailed'));
      }
      const assertion = preparePublicKeyCredential(credential);
      await handleStart({ assertion });
    } catch (err) {
      const webAuthnError = getWebAuthnError(err);
      if (webAuthnError.code !== 'user_cancelled') {
        setError(webAuthnError.message || I18n.t('Impersonation.StepUpFailed'));
      }
      setLoading(false);
    }
  }, [handleStart, stepUpOptions]);

  const targetLabel =
    targetEmployee?.name ||
    [targetEmployee?.firstName, targetEmployee?.surName].filter(Boolean).join(' ') ||
    targetUser?.phone ||
    targetUser?._id;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{I18n.t('Impersonation.StepUpTitle')}</DialogTitle>
      <DialogContent>
        <Box component="p" sx={{ mt: 0 }}>
          {I18n.t('Impersonation.StepUpDescription', { name: targetLabel })}
        </Box>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Box component="form" onSubmit={handlePasswordSubmit}>
          <TextField
            fullWidth
            type="password"
            label={I18n.t('Impersonation.PasswordLabel')}
            value={password}
            onChange={event => setPassword(event.target.value)}
            disabled={loading}
            margin="normal"
            size="small"
            inputRef={passwordInputRef}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {I18n.t('Common.cancel')}
        </Button>
        {passkeyAvailable ? (
          <Button variant="outlined" onClick={handlePasskey} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : I18n.t('Impersonation.UsePasskey')}
          </Button>
        ) : null}
        <Button variant="contained" onClick={handlePasswordSubmit} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : I18n.t('Impersonation.Confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ImpersonationStepUpDialog.propTypes = {
  open: bool,
  onClose: func.isRequired,
  onSuccess: func,
  resetCompany: bool,
  targetUser: shape({
    _id: string,
    role: number,
    name: string,
    firstName: string,
    surName: string,
    phone: string,
  }),
  targetEmployee: shape({
    _id: string,
    name: string,
    firstName: string,
    surName: string,
    user: shape({
      _id: string,
      role: number,
      name: string,
      firstName: string,
      surName: string,
      phone: string,
    }),
  }),
};

export default ImpersonationStepUpDialog;
